import { ArrowRight, ShieldAlert, ShieldCheck, ShieldX } from 'lucide-react';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../api/errors';
import { useUserMap } from '../../api/org';
import { type ApprovalDecision, useArchiveTask, useCompleteTask, useDecideApproval, useReopenTask, useUpdateTask } from '../../api/tasks';
import type { Task, UpdateTaskInput } from '../../api/types';
import { useSession } from '../../app/session';
import { HelpHint } from '../training/HelpHint';
import { Button } from '../../components/ui/Button';
import { SearchList } from '../../components/ui/Combobox';
import { DatePanel } from '../../components/ui/DatePicker';
import { ConfirmDialog, Dialog } from '../../components/ui/Dialog';
import { Field, FormAlert, Textarea } from '../../components/ui/Field';
import { useToast } from '../../components/ui/Toast';
import { cn } from '../../lib/cn';
import { formatDate } from '../../lib/dates';
import { useCopy } from '../../lib/hooks';
import { describeSensitive, detectSensitive, maskSensitive } from '../../lib/sensitive';
import { ReasonStep, useUserOptions } from './TaskControls';
import { lateAssignSuggestion, taskUrl } from './taskUtils';

type Actions = {
  complete: (task: Task) => void;
  reopen: (task: Task) => void;
  decide: (task: Task, decision?: ApprovalDecision) => void;
  archive: (task: Task) => void;
  reschedule: (task: Task) => void;
  reassign: (task: Task) => void;
  copyLink: (task: Task) => void;
  /** Натискання на кружок завершення: завершити, ухвалити рішення або відкрити знову. */
  toggle: (task: Task) => void;
};

const Ctx = createContext<Actions | null>(null);

export function useTaskActions(): Actions {
  const c = useContext(Ctx);
  if (!c) throw new Error('TaskActionsProvider відсутній');
  return c;
}

type State =
  | { kind: 'complete'; task: Task }
  | { kind: 'reopen'; task: Task }
  | { kind: 'decide'; task: Task; decision: ApprovalDecision }
  | { kind: 'archive'; task: Task }
  | { kind: 'reschedule'; task: Task }
  | { kind: 'reassign'; task: Task }
  | null;

/** Діалоги дій із задачею — один екземпляр на оболонку; викликаються з рядків, карток і панелі. */
export function TaskActionsProvider({ children, onOpenTask }: { children: ReactNode; onOpenTask: (id: string) => void }) {
  const [state, setState] = useState<State>(null);
  const toast = useToast();
  const [, copy] = useCopy();
  const { userId, isOwner } = useSession();

  const close = useCallback(() => setState(null), []);

  const actions = useMemo<Actions>(
    () => ({
      complete: (task) => setState({ kind: 'complete', task }),
      reopen: (task) => setState({ kind: 'reopen', task }),
      decide: (task, decision = 'approved') => setState({ kind: 'decide', task, decision }),
      archive: (task) => setState({ kind: 'archive', task }),
      reschedule: (task) => setState({ kind: 'reschedule', task }),
      reassign: (task) => setState({ kind: 'reassign', task }),
      copyLink: (task) => {
        void copy(taskUrl(task.id)).then((ok) => (ok ? toast.success(`Посилання на ${task.key} скопійовано`) : toast.error('Не вдалося скопіювати посилання')));
      },
      toggle: (task) => {
        if (task.status === 'completed') setState({ kind: 'reopen', task });
        else if (task.subtype === 'approval' && task.approvalStatus === 'pending') {
          if (task.assigneeId === userId || isOwner) setState({ kind: 'decide', task, decision: 'approved' });
          else toast.info('Рішення ухвалює призначений погоджувач', `${task.key}: дочекайтеся рішення виконавця задачі.`);
        } else setState({ kind: 'complete', task });
      },
    }),
    [copy, toast, userId, isOwner],
  );

  return (
    <Ctx.Provider value={actions}>
      {children}
      <CompleteDialog state={state?.kind === 'complete' ? state.task : null} onClose={close} onOpenTask={onOpenTask} />
      <ReopenDialog task={state?.kind === 'reopen' ? state.task : null} onClose={close} />
      <DecisionDialog task={state?.kind === 'decide' ? state.task : null} initial={state?.kind === 'decide' ? state.decision : 'approved'} onClose={close} />
      <ArchiveDialog task={state?.kind === 'archive' ? state.task : null} onClose={close} />
      <RescheduleDialog task={state?.kind === 'reschedule' ? state.task : null} onClose={close} />
      <ReassignDialog task={state?.kind === 'reassign' ? state.task : null} onClose={close} />
    </Ctx.Provider>
  );
}

function TaskCaption({ task }: { task: Task }) {
  return (
    <span className="flex min-w-0 items-baseline gap-2">
      <span className="key text-fg-3">{task.key}</span>
      <span className="truncate">{task.title}</span>
    </span>
  );
}

function SensitiveHint({ text, onMask }: { text: string; onMask: () => void }) {
  const found = detectSensitive(text);
  if (!found.length) return null;
  return (
    <FormAlert tone="warning" title="Схоже, у тексті є чутливі дані" detail={`Виявлено: ${describeSensitive(found)}. Такі дані не зберігають у задачах — замаскуйте їх.`}>
      <Button size="sm" variant="secondary" className="mt-2" onClick={onMask}>
        Замаскувати
      </Button>
    </FormAlert>
  );
}

function CompleteDialog({ state: task, onClose, onOpenTask }: { state: Task | null; onClose: () => void; onOpenTask: (id: string) => void }) {
  const { policy } = useSession();
  const [result, setResult] = useState('');
  const [touched, setTouched] = useState(false);
  const complete = useCompleteTask();
  const toast = useToast();
  const [lastTask, setLastTask] = useState<Task | null>(null);
  if (task && task !== lastTask) {
    setLastTask(task);
    setResult('');
    setTouched(false);
  }
  useEffect(() => {
    if (task) complete.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task]);
  const t = task ?? lastTask;
  const required = policy.requireResultOnComplete;
  const localError = touched && required && result.trim().length < 3 ? 'Опишіть результат виконання (щонайменше 3 символи).' : null;
  const serverError = complete.error instanceof ApiError ? complete.error : null;

  const submit = () => {
    setTouched(true);
    if (!t || (required && result.trim().length < 3)) return;
    complete.mutate(
      { id: t.id, result: result.trim() || undefined },
      {
        onSuccess: (done) => {
          onClose();
          toast.success(`Задачу ${done.key} завершено`, 'Результат збережено в історії задачі.', { label: 'Відкрити картку', onClick: () => onOpenTask(done.id) });
        },
        onError: (e) => {
          // Немає мережі: завершення в черзі — закриваємо діалог, повідомлення покаже оболонка.
          if (e instanceof ApiError && e.isQueued) {
            complete.reset();
            onClose();
          }
        },
      },
    );
  };

  return (
    <Dialog
      open={!!task}
      onClose={onClose}
      title="Завершити задачу"
      description={t ? <TaskCaption task={t} /> : undefined}
      onSubmit={submit}
      dismissible={!result}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Скасувати
          </Button>
          <Button type="submit" variant="primary" loading={complete.isPending}>
            Завершити задачу
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {t && t.openSubtaskCount > 0 && (
          <FormAlert tone="info" detail={`Відкритих підзадач: ${t.openSubtaskCount}. Вони залишаться відкритими — перевірте, чи це так задумано.`} />
        )}
        <Field
          aside={<HelpHint id="task.result" />}
          label="Результат"
          required={required}
          requiredNote="за правилами фірми"
          error={localError ?? serverError?.fieldError('result')}
          hint="Що зроблено: документ, рішення, посилання на файл. Результат побачать автор і учасники."
        >
          <Textarea
            data-autofocus
            rows={5}
            value={result}
            onChange={(e) => setResult(e.target.value)}
           
            placeholder="Наприклад: підготовлено й надіслано клієнту заяву; копія в папці клієнта"
          />
        </Field>
        <SensitiveHint text={result} onMask={() => setResult(maskSensitive(result))} />
        {serverError && !serverError.fieldError('result') && <FormAlert detail={serverError.detail} items={serverError.errors.map((e) => e.message)} />}
      </div>
    </Dialog>
  );
}

function ReopenDialog({ task, onClose }: { task: Task | null; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const reopen = useReopenTask();
  const toast = useToast();
  const [last, setLast] = useState<Task | null>(null);
  if (task && task !== last) {
    setLast(task);
    setReason('');
    setTouched(false);
  }
  useEffect(() => {
    if (task) reopen.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task]);
  const t = task ?? last;
  const localError = touched && reason.trim().length < 3 ? 'Вкажіть причину (щонайменше 3 символи).' : null;
  return (
    <Dialog
      open={!!task}
      onClose={onClose}
      title="Відкрити задачу знову"
      description={t ? <TaskCaption task={t} /> : undefined}
      size="sm"
      onSubmit={() => {
        setTouched(true);
        if (!t || reason.trim().length < 3) return;
        reopen.mutate(
          { id: t.id, reason: reason.trim() },
          {
            onSuccess: (x) => {
              onClose();
              toast.success(`Задачу ${x.key} відкрито знову`);
            },
          },
        );
      }}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Скасувати
          </Button>
          <Button type="submit" variant="primary" loading={reopen.isPending}>
            Відкрити знову
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Причина повторного відкриття" required error={localError} hint="Причина потрапить у коментарі й історію змін.">
          <Textarea data-autofocus rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        {reopen.error instanceof ApiError && <FormAlert detail={reopen.error.detail} items={reopen.error.errors.map((e) => e.message)} />}
      </div>
    </Dialog>
  );
}

const DECISIONS: { value: ApprovalDecision; label: string; hint: string; icon: ReactNode; cls: string }[] = [
  { value: 'approved', label: 'Погодити', hint: 'Результат прийнято без змін', icon: <ShieldCheck className="size-4" aria-hidden />, cls: 'text-success-fg' },
  { value: 'changes_requested', label: 'На доопрацювання', hint: 'Потрібні зміни — опишіть які', icon: <ShieldAlert className="size-4" aria-hidden />, cls: 'text-warning-fg' },
  { value: 'rejected', label: 'Відхилити', hint: 'Не погоджено — поясніть чому', icon: <ShieldX className="size-4" aria-hidden />, cls: 'text-danger-fg' },
];

function DecisionDialog({ task, initial, onClose }: { task: Task | null; initial: ApprovalDecision; onClose: () => void }) {
  const [decision, setDecision] = useState<ApprovalDecision>(initial);
  const [comment, setComment] = useState('');
  const [touched, setTouched] = useState(false);
  const decide = useDecideApproval();
  const toast = useToast();
  const [last, setLast] = useState<Task | null>(null);
  if (task && task !== last) {
    setLast(task);
    setDecision(initial);
    setComment('');
    setTouched(false);
  }
  useEffect(() => {
    if (task) decide.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task]);
  const t = task ?? last;
  const needsComment = decision !== 'approved';
  const localError = touched && needsComment && comment.trim().length < 3 ? 'Поясніть рішення, щоб виконавець знав, що змінити.' : null;
  return (
    <Dialog
      open={!!task}
      onClose={onClose}
      title="Рішення щодо погодження"
      description={t ? <TaskCaption task={t} /> : undefined}
      onSubmit={() => {
        setTouched(true);
        if (!t || (needsComment && comment.trim().length < 3)) return;
        decide.mutate(
          { id: t.id, decision, comment: comment.trim() || undefined },
          {
            onSuccess: (x) => {
              onClose();
              toast.success(`${DECISIONS.find((d) => d.value === decision)?.label ?? 'Рішення'}: ${x.key}`, 'Автор задачі отримає сповіщення.');
            },
          },
        );
      }}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Скасувати
          </Button>
          <Button type="submit" variant="primary" loading={decide.isPending}>
            Ухвалити рішення
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <fieldset className="grid gap-2 sm:grid-cols-3">
          <legend className="mb-1.5 text-meta font-medium">Рішення</legend>
          {DECISIONS.map((d) => (
            <label
              key={d.value}
              className={cn(
                'flex cursor-pointer flex-col gap-1 rounded-sm border px-3 py-2.5 transition-colors',
                decision === d.value ? 'border-accent bg-accent-soft' : 'border-line hover:bg-surface-2',
              )}
            >
              <span className={cn('flex items-center gap-2 text-body font-medium', d.cls)}>
                <input
                  type="radio"
                  name="decision"
                  value={d.value}
                  checked={decision === d.value}
                  onChange={() => setDecision(d.value)}
                  className="size-3.5 accent-[var(--c-accent)]"
                />
                {d.icon}
                {d.label}
              </span>
              <span className="text-caption text-fg-3">{d.hint}</span>
            </label>
          ))}
        </fieldset>
        <Field label="Коментар" required={needsComment} error={localError}>
          <Textarea data-autofocus rows={4} value={comment} onChange={(e) => setComment(e.target.value)} />
        </Field>
        {decide.error instanceof ApiError && <FormAlert detail={decide.error.detail} items={decide.error.errors.map((e) => e.message)} />}
      </div>
    </Dialog>
  );
}

function ArchiveDialog({ task, onClose }: { task: Task | null; onClose: () => void }) {
  const archive = useArchiveTask();
  const toast = useToast();
  const [last, setLast] = useState<Task | null>(null);
  if (task && task !== last) setLast(task);
  useEffect(() => {
    if (task) archive.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task]);
  const t = task ?? last;
  return (
    <ConfirmDialog
      open={!!task}
      onClose={onClose}
      title={t ? `Архівувати ${t.key}?` : 'Архівувати задачу?'}
      description="Задача зникне з переліків і дошок, але історія змін і коментарі збережуться. Видалення в ЄРС немає — лише архів."
      confirmLabel="Архівувати"
      tone="danger"
      loading={archive.isPending}
      onConfirm={() => {
        if (!t) return;
        archive.mutate(t.id, {
          onSuccess: (x) => {
            onClose();
            toast.success(`Задачу ${x.key} архівовано`);
          },
        });
      }}
    >
      {archive.error instanceof ApiError && (
        <div className="mt-3">
          <FormAlert detail={archive.error.detail} />
        </div>
      )}
    </ConfirmDialog>
  );
}

/** Перенесення строку з контекстного меню: дата → причина (за правилами фірми). */
function RescheduleDialog({ task, onClose }: { task: Task | null; onClose: () => void }) {
  const { policy } = useSession();
  const update = useUpdateTask();
  const toast = useToast();
  const [date, setDate] = useState<string | null | undefined>(undefined);
  const [last, setLast] = useState<Task | null>(null);
  if (task && task !== last) {
    setLast(task);
    setDate(undefined);
  }
  useEffect(() => {
    if (task) update.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task]);
  const t = task ?? last;
  const needsReason = !!t?.dueOn && policy.requireReasonOnDueChange;
  const save = (d: string | null, reason?: string) => {
    if (!t) return;
    const body: UpdateTaskInput = { dueOn: d };
    if (reason) body.changeReason = reason;
    update.mutate(
      { task: t, body },
      {
        onSuccess: () => {
          onClose();
          toast.success(d ? `Строк ${t.key}: ${formatDate(d)}` : `Строк ${t.key} знято`);
        },
        onError: () => setDate(d),
      },
    );
  };
  return (
    <Dialog open={!!task} onClose={onClose} title="Перенести строк" description={t ? <TaskCaption task={t} /> : undefined} size="md" bodyClassName="p-0">
      {date === undefined ? (
        <DatePanel
          value={t?.dueOn ?? null}
          allowClear={!!t?.dueOn}
          clearLabel="Зняти строк"
          onSelect={(d) => {
            if (!t || d === t.dueOn) return onClose();
            if (needsReason) setDate(d);
            else save(d);
          }}
        />
      ) : (
        <ReasonStep
          className="p-5"
          title="Причина перенесення"
          summary={
            <span className="tnum flex flex-wrap items-center gap-1.5">
              <span>{t?.dueOn ? formatDate(t.dueOn) : 'Без строку'}</span>
              <ArrowRight className="size-3.5" aria-hidden />
              <span className="font-medium text-fg">{date ? formatDate(date) : 'Без строку'}</span>
            </span>
          }
          reasonLabel="Причина перенесення"
          reasonRequired={needsReason}
          reasonHint="Причину побачать учасники задачі; вона збережеться в історії змін."
          submitLabel="Перенести строк"
          onBack={() => {
            setDate(undefined);
            update.reset();
          }}
          onSubmit={(reason) => save(date, reason)}
          pending={update.isPending}
          error={update.error}
        />
      )}
    </Dialog>
  );
}

/** Зміна виконавця з контекстного меню: людина → причина й пропозиція строку після 15:00. */
function ReassignDialog({ task, onClose }: { task: Task | null; onClose: () => void }) {
  const { policy, userId } = useSession();
  const users = useUserMap();
  const options = useUserOptions();
  const update = useUpdateTask();
  const toast = useToast();
  const [picked, setPicked] = useState<string | null>(null);
  const [moveDue, setMoveDue] = useState(true);
  const [last, setLast] = useState<Task | null>(null);
  if (task && task !== last) {
    setLast(task);
    setPicked(null);
    setMoveDue(true);
  }
  useEffect(() => {
    if (task) update.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task]);
  const t = task ?? last;
  const current = t?.assigneeId ? users.get(t.assigneeId) : undefined;
  const next = picked ? users.get(picked) : undefined;
  const suggestion = t && picked ? lateAssignSuggestion(policy, picked, userId, t.dueOn) : null;
  const reasonNeeded = !!t?.assigneeId && policy.requireReasonOnReassign;
  const dueReason = !!suggestion && moveDue && !!t?.dueOn && policy.requireReasonOnDueChange;

  const save = (id: string, reason?: string, due?: string) => {
    if (!t) return;
    const body: UpdateTaskInput = { assigneeId: id };
    if (due) body.dueOn = due;
    if (reason) body.changeReason = reason;
    update.mutate(
      { task: t, body },
      {
        onSuccess: () => {
          onClose();
          toast.success(`Виконавець ${t.key}: ${users.get(id)?.name ?? ''}`, due ? `Строк: ${formatDate(due)}` : undefined);
        },
        onError: () => setPicked(id),
      },
    );
  };

  return (
    <Dialog open={!!task} onClose={onClose} title="Змінити виконавця" description={t ? <TaskCaption task={t} /> : undefined} size="md" bodyClassName="p-0">
      {!picked ? (
        <SearchList
          autoFocus
          options={options}
          value={t?.assigneeId ?? null}
          label="Новий виконавець"
          placeholder="Пошук працівника"
          listClassName="max-h-[50dvh]"
          header={<p className="border-b border-line px-5 py-2.5 text-meta text-fg-2">Одна задача — один виконавець.{reasonNeeded ? ' Зміна виконавця — з причиною.' : ''}</p>}
          onSelect={(id) => {
            if (!t || id === t.assigneeId) return onClose();
            const late = lateAssignSuggestion(policy, id, userId, t.dueOn);
            if (!reasonNeeded && !late) save(id);
            else setPicked(id);
          }}
        />
      ) : (
        <ReasonStep
          className="p-5"
          title="Причина зміни виконавця"
          summary={
            <span className="flex flex-wrap items-center gap-1.5">
              <span>{current?.name ?? 'Не призначено'}</span>
              <ArrowRight className="size-3.5" aria-hidden />
              <span className="font-medium text-fg">{next?.name}</span>
            </span>
          }
          extra={
            suggestion ? (
              <div className="rounded-sm border border-info-line bg-info-soft px-2.5 py-2 text-meta text-info-fg">
                <p>Після {policy.lateAssignHour}:00 доручення іншому працівнику ставлять не раніше ніж на наступний робочий день.</p>
                <label className="mt-1.5 flex items-center gap-2 font-medium">
                  <input type="checkbox" checked={moveDue} onChange={(e) => setMoveDue(e.target.checked)} className="size-3.5 accent-[var(--c-accent)]" />
                  Перенести строк на {formatDate(suggestion)}
                </label>
              </div>
            ) : null
          }
          reasonLabel={reasonNeeded ? 'Причина зміни виконавця' : 'Причина (для історії змін)'}
          reasonRequired={reasonNeeded || dueReason}
          submitLabel="Змінити виконавця"
          onBack={() => {
            setPicked(null);
            update.reset();
          }}
          onSubmit={(reason) => save(picked, reason || undefined, suggestion && moveDue ? suggestion : undefined)}
          pending={update.isPending}
          error={update.error}
        />
      )}
    </Dialog>
  );
}
